/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useQuery } from '@tanstack/react-query';

import { PropertiesService } from '@/services/requests/housien-services/PropertiesService';

export interface SalePropertySummary {
  /** The property's headline, or the raw id when it cannot be resolved. */
  label: string;
  /** Neighbourhood and city, when the property carries them. */
  locality: string;
}

/**
 * Resolves the property behind a sale into something a broker can read.
 *
 * The Sale record stores only `property_id` — an opaque handle like
 * `JT6UaDMQWpQ`. Printing that in the header would name the property in a way
 * nobody can recognise, so the headline is fetched and the id kept only as the
 * fallback for when it cannot be.
 *
 * Deliberately on its own query key rather than under `['sales', agencyId]`:
 * the property does not change when the sale does, and refetching it after
 * every transition would be work with no new answer.
 *
 * @param agencyId - The owning agency.
 * @param propertyId - The property the sale is for.
 * @returns The readable label and locality.
 */
export function useSaleProperty(
  agencyId: string,
  propertyId: string | undefined,
): SalePropertySummary {
  const { data } = useQuery({
    queryKey: ['sale-property', agencyId, propertyId],
    queryFn: () => PropertiesService.getProperty(agencyId, propertyId as string),
    enabled: Boolean(agencyId && propertyId),
    staleTime: 5 * 60 * 1000,
  });

  const record = data as
    | { description?: { headline?: string }; location?: { neighborhood?: string; city?: string } }
    | undefined;

  const headline = record?.description?.headline;

  // The headline already ends in the neighbourhood and city ("...1 vaga em
  // Camorim, Rio de Janeiro"), so appending them again just repeats itself.
  // The locality is only worth showing when there is no headline to carry it
  // and the id is all we have left.
  const locality = headline
    ? ''
    : [record?.location?.neighborhood, record?.location?.city].filter(Boolean).join(', ');

  return {
    label: headline || propertyId || '',
    locality,
  };
}
